import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input-simple';
import { Button } from '../../components/ui/button-simple';
import { Search, MapPin, Factory, Eye, AlertCircle, ChevronLeft, ChevronRight, X, Building2, Users, Globe, Phone, Plus, TrendingUp, Package, ShieldCheck, Tag, MessageSquare } from 'lucide-react';
import { municipalities } from '../../data/municipalities';
import { processorsAPI, profileRequestAPI } from '../../services/api';
import { useProcessors } from '../../hooks/useApi';
import { RequestPostingModal } from '../../components/forms/RequestPostingModal';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .animate-slide-down { animation: slideDown 0.6s ease-out forwards; }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .animate-slide-up { animation: slideUp 0.4s ease-out forwards; }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-processor-account-animations]')) {
    styleSheet.setAttribute('data-processor-account-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function ProcessorsAccountDirectory({ user }) {
  const { processors, loading, error, fetchProcessors } = useProcessors();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedMarketReach, setSelectedMarketReach] = useState('all');
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [showPostingModal, setShowPostingModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const loadData = async () => {
      try { await fetchProcessors(() => processorsAPI.getAll()); } catch {}
    };
    loadData();
  }, [fetchProcessors]);

  const filteredProcessors = useMemo(() => {
    return processors.filter(processor => {
      if (processor.id === null || processor.id === undefined) return false;
      const matchesSearch =
        (processor.businessName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (processor.ownerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (processor.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (processor.municipality || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMunicipality = selectedMunicipality === 'all' ||
        (processor.municipality || processor.address || '').includes(selectedMunicipality);
      const matchesMarketReach = selectedMarketReach === 'all' ||
        (processor.marketReach || '').toLowerCase().includes(selectedMarketReach.toLowerCase());
      return matchesSearch && matchesMunicipality && matchesMarketReach;
    });
  }, [processors, searchTerm, selectedMunicipality, selectedMarketReach]);

  const paginatedProcessors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProcessors.slice(startIndex, endIndex);
  }, [filteredProcessors, currentPage, itemsPerPage]);

  const navigate = useNavigate();
  const totalPages = Math.ceil(filteredProcessors.length / itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedMunicipality, selectedMarketReach]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':   return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'inactive': return 'bg-rose-100 text-rose-800 border-rose-300';
      default:         return 'bg-amber-100 text-amber-800 border-amber-300';
    }
  };

  const getMarketReachColor = (reach) => {
    switch (reach?.toLowerCase()) {
      case 'international': return 'bg-purple-100 text-purple-800';
      case 'national':      return 'bg-blue-100 text-blue-800';
      case 'regional':      return 'bg-teal-100 text-teal-800';
      default:              return 'bg-gray-100 text-gray-800';
    }
  };

  const [postingRequest, setPostingRequest] = useState(null);

  useEffect(() => {
    if (user?.id) {
      profileRequestAPI.getMyRequest(user.id)
        .then(res => { if (res.success) setPostingRequest(res.request); })
        .catch(() => {});
    }
  }, [user]);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-amber-50/50 via-orange-50/30 to-amber-100/40 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-8 bg-gradient-to-r from-amber-700 via-orange-700 to-amber-800 rounded-2xl shadow-xl p-6 text-white animate-slide-down relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-md p-3.5 rounded-2xl shadow-inner">
                  <Factory className="h-8 w-8 text-amber-200" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-extrabold tracking-tight">Cacao Processors Directory</h1>
                    <span className="bg-amber-500/40 text-amber-100 border border-amber-300/40 text-xs px-2.5 py-0.5 rounded-full font-medium">
                      Member Verified Access
                    </span>
                  </div>
                  <p className="text-amber-100 max-w-2xl text-sm mt-1">
                    Full directory access for registered Industry Accounts — view complete business details, capacity, products, and contact info.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Action Banner for Processors */}
          {user?.role === 'processor' && (
            postingRequest ? (
              <div className="mb-6 bg-gradient-to-r from-amber-700 to-orange-700 rounded-2xl shadow-lg p-4 text-white flex items-center justify-between gap-4 border border-amber-400/30">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-amber-200 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Public Listing Status: <span className="uppercase font-extrabold text-amber-200">{postingRequest.status}</span></p>
                    <p className="text-xs text-amber-100">You can only request public listing once. You can edit your profile details in your Profile tab.</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  className="flex-shrink-0 flex items-center gap-2 bg-white text-amber-900 font-bold text-xs px-4 py-2.5 rounded-xl hover:bg-amber-50 shadow transition"
                >
                  Manage in Profile
                </button>
              </div>
            ) : (
              <div className="mb-6 bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl shadow-lg p-4 text-white flex items-center justify-between gap-4 border border-amber-400/30">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-amber-200 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Are you a Cacao Processor?</p>
                    <p className="text-xs text-amber-100">Request to publish your business profile in this verified directory (One-time request).</p>
                  </div>
                </div>
                <button
                  id="btn-processor-account-quick-request"
                  onClick={() => setShowPostingModal(true)}
                  className="flex-shrink-0 flex items-center gap-2 bg-white text-amber-900 font-bold text-xs px-4 py-2.5 rounded-xl hover:bg-amber-50 shadow transition"
                >
                  <Plus className="w-4 h-4" />
                  Request Public Listing
                </button>
              </div>
            )
          )}

          {/* Search and Filters */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-amber-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search business name, owner or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 border-amber-200 focus:border-amber-500 focus:ring-amber-500 rounded-xl text-sm"
                  aria-label="Search processors"
                />
              </div>

              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500 h-5 w-5 z-10" />
                <select
                  value={selectedMunicipality}
                  onChange={(e) => setSelectedMunicipality(e.target.value)}
                  className="pl-10 w-full h-12 border border-amber-200 rounded-xl focus:border-amber-500 focus:ring-amber-500 bg-white appearance-none cursor-pointer text-sm"
                  aria-label="Filter by municipality"
                >
                  <option value="all">All Municipalities</option>
                  {municipalities.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500 h-5 w-5 z-10" />
                <select
                  value={selectedMarketReach}
                  onChange={(e) => setSelectedMarketReach(e.target.value)}
                  className="pl-10 w-full h-12 border border-amber-200 rounded-xl focus:border-amber-500 focus:ring-amber-500 bg-white appearance-none cursor-pointer text-sm"
                  aria-label="Filter by market reach"
                >
                  <option value="all">All Market Reach</option>
                  <option value="local">Local</option>
                  <option value="regional">Regional</option>
                  <option value="national">National</option>
                  <option value="international">International</option>
                </select>
              </div>
            </div>

            <div className="mt-4 text-sm flex items-center justify-between">
              <span className="text-gray-600">
                Showing <span className="font-bold text-amber-700">{filteredProcessors.length}</span> registered cacao processors
              </span>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
                <p className="text-amber-800 font-medium">Loading processor directory...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-800 font-medium">Error loading processors</span>
              </div>
              <p className="text-red-600 mt-2">{error}</p>
              <Button onClick={() => window.location.reload()} className="mt-4 bg-red-600 hover:bg-red-700">
                Retry
              </Button>
            </div>
          )}

          {/* Processors Grid */}
          {!loading && !error && filteredProcessors.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedProcessors.map((processor) => (
                  <div
                    key={processor.id}
                    className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-amber-100 hover:border-amber-300 overflow-hidden flex flex-col animate-fade-in-up"
                    style={{ height: '400px' }}
                  >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-3.5 border-b border-amber-100 flex-shrink-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base text-amber-950 font-bold group-hover:text-amber-700 transition-colors truncate">
                            {processor.businessName || 'Unnamed Business'}
                          </h3>
                          <div className="flex items-center mt-1 text-gray-600 text-xs">
                            <MapPin className="h-3.5 w-3.5 mr-1 text-amber-600 flex-shrink-0" />
                            <span className="truncate">{processor.address || processor.municipality || 'Bohol'}</span>
                          </div>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${getStatusColor(processor.businessStatus)} flex-shrink-0 ml-2`}>
                          {processor.businessStatus || 'Active'}
                        </span>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-3 flex-1 flex flex-col overflow-y-auto">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center justify-between p-2 bg-amber-50/50 rounded-xl text-xs">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-amber-600" />
                            <span className="text-gray-600 font-medium">Owner/Manager</span>
                          </div>
                          <span className="font-bold text-gray-900 truncate max-w-[50%]">{processor.ownerName || 'N/A'}</span>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-amber-50/50 rounded-xl text-xs">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-blue-600" />
                            <span className="text-gray-600 font-medium">Type</span>
                          </div>
                          <span className="font-semibold text-blue-800 bg-blue-50 px-2 py-0.5 rounded-md">{processor.businessType || 'Single'}</span>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-amber-50/50 rounded-xl text-xs">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-orange-600" />
                            <span className="text-gray-600 font-medium">Capacity</span>
                          </div>
                          <span className="font-bold text-gray-900">{processor.processingCapacity ? `${processor.processingCapacity} kg/mo` : 'N/A'}</span>
                        </div>

                        {processor.marketReach && (
                          <div className="flex items-center justify-between p-2 bg-amber-50/50 rounded-xl text-xs">
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4 text-purple-600" />
                              <span className="text-gray-600 font-medium">Market Reach</span>
                            </div>
                            <span className={`px-2 py-0.5 rounded-md font-semibold ${getMarketReachColor(processor.marketReach)}`}>
                              {processor.marketReach}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-3 flex gap-2 flex-shrink-0">
                        <Button
                          className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold text-xs flex items-center justify-center gap-1.5 rounded-xl shadow transition-all py-2"
                          onClick={() => setSelectedProcessor(processor)}
                        >
                          <Eye className="h-4 w-4" />
                          View Profile
                        </Button>
                        <Button
                          className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs flex items-center justify-center gap-1.5 rounded-xl shadow transition-all px-3.5 py-2"
                          onClick={() => navigate(`/messages?partnerId=processor_${processor.id}&partnerName=${encodeURIComponent(processor.businessName || processor.nameOfBusiness || 'Processor')}&partnerRole=processor`)}
                          title="Message Processor"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Message
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-8 bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Page</span>
                    <span className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-md">
                      {currentPage}
                    </span>
                    <span className="text-sm text-gray-600">of {totalPages}</span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {filteredProcessors.length === 0 && !loading && !error && (
            <div className="text-center py-16 bg-white rounded-2xl border border-amber-100 shadow-sm">
              <Factory className="h-14 w-14 text-amber-300 mx-auto mb-3" />
              <h3 className="text-gray-700 font-semibold text-base mb-1">No processors found</h3>
              <p className="text-gray-500 text-sm">Try adjusting your search or filters.</p>
            </div>
          )}

          {/* Processor Full Info Detail Modal */}
          {selectedProcessor && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 animate-fade-in backdrop-blur-sm">
              <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-slide-up flex flex-col">
                <div className="bg-gradient-to-r from-amber-700 to-orange-800 p-5 text-white flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2.5 rounded-xl">
                      <Factory className="h-6 w-6 text-amber-200" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">{selectedProcessor.businessName}</h2>
                      <p className="text-amber-100 text-xs">{selectedProcessor.address || selectedProcessor.municipality || 'Processor Profile'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedProcessor(null)}
                    className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-xl transition"
                    aria-label="Close profile"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4 p-6 overflow-y-auto flex-1 text-sm text-gray-700">
                  {/* Business Overview */}
                  <div className="bg-amber-50/60 p-4 rounded-xl border border-amber-100">
                    <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-amber-600" /> Business Overview
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-gray-500">Business Name:</span> <strong className="text-gray-900">{selectedProcessor.businessName}</strong></div>
                      <div><span className="text-gray-500">Owner/Manager:</span> <strong className="text-gray-900">{selectedProcessor.ownerName || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Business Address:</span> <strong className="text-gray-900">{selectedProcessor.address || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Contact Number:</span> <strong className="text-gray-900">{selectedProcessor.contactNumber || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Business Type:</span> <strong className="text-gray-900">{selectedProcessor.businessType || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Status:</span> <strong className="text-gray-900">{selectedProcessor.businessStatus || 'Active'}</strong></div>
                      <div><span className="text-gray-500">Years in Operation:</span> <strong className="text-gray-900">{selectedProcessor.yearsInOperation ? `${selectedProcessor.yearsInOperation} yrs` : 'N/A'}</strong></div>
                      <div><span className="text-gray-500">No. of Employees:</span> <strong className="text-gray-900">{selectedProcessor.noOfEmployees || 'N/A'}</strong></div>
                    </div>
                  </div>

                  {/* Processing & Capacity */}
                  <div className="bg-amber-50/60 p-4 rounded-xl border border-amber-100">
                    <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4 text-amber-600" /> Processing & Capacity
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-gray-500">Processing Capacity:</span> <strong className="text-gray-900">{selectedProcessor.processingCapacity ? `${selectedProcessor.processingCapacity} kg/mo` : 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Processing Frequency:</span> <strong className="text-gray-900">{selectedProcessor.processingFrequency || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Source of Cacao:</span> <strong className="text-gray-900">{selectedProcessor.sourceOfCacao || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Cacao Form:</span> <strong className="text-gray-900">{selectedProcessor.cacaoForm || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Preferred Variety:</span> <strong className="text-gray-900">{selectedProcessor.preferredVariety || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Processed Variety:</span> <strong className="text-gray-900">{selectedProcessor.processedVariety || 'N/A'}</strong></div>
                    </div>
                  </div>

                  {/* Products & Marketing */}
                  <div className="bg-amber-50/60 p-4 rounded-xl border border-amber-100">
                    <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-amber-600" /> Products & Market Reach
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-gray-500">Product Types:</span> <strong className="text-gray-900">{selectedProcessor.productTypes || selectedProcessor.products || 'Tablea / Chocolate'}</strong></div>
                      <div><span className="text-gray-500">Market Reach:</span> <strong className="text-amber-800">{selectedProcessor.marketReach || 'Local'}</strong></div>
                      <div><span className="text-gray-500">Marketing Type:</span> <strong className="text-gray-900">{selectedProcessor.marketingType || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Marketplace:</span> <strong className="text-gray-900">{selectedProcessor.marketplace || 'N/A'}</strong></div>
                    </div>
                  </div>

                  {/* Financials & Insights */}
                  {(selectedProcessor.totalInvestment || selectedProcessor.annualIncome || selectedProcessor.challenges) && (
                    <div className="bg-amber-50/60 p-4 rounded-xl border border-amber-100">
                      <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-amber-600" /> Financials & Business Insights
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {selectedProcessor.totalInvestment && <div><span className="text-gray-500">Total Investment:</span> <strong className="text-gray-900">₱{Number(selectedProcessor.totalInvestment).toLocaleString()}</strong></div>}
                        {selectedProcessor.annualIncome && <div><span className="text-gray-500">Annual Income:</span> <strong className="text-green-700">₱{Number(selectedProcessor.annualIncome).toLocaleString()}</strong></div>}
                        {selectedProcessor.challenges && <div className="col-span-2"><span className="text-gray-500">Challenges Faced:</span> <strong className="text-gray-900">{selectedProcessor.challenges}</strong></div>}
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0 bg-gray-50">
                  <Button
                    onClick={() => {
                      const p = selectedProcessor;
                      setSelectedProcessor(null);
                      navigate(`/messages?partnerId=processor_${p.id}&partnerName=${encodeURIComponent(p.businessName || p.nameOfBusiness || 'Processor')}&partnerRole=processor`);
                    }}
                    className="px-5 bg-amber-600 hover:bg-amber-700 text-white font-semibold flex items-center gap-2 rounded-xl"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Message Processor
                  </Button>
                  <Button
                    onClick={() => setSelectedProcessor(null)}
                    className="px-6 bg-amber-700 hover:bg-amber-800 text-white rounded-xl"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Posting Request Modal for Processor role */}
      {showPostingModal && user && (
        <RequestPostingModal
          user={user}
          onClose={() => setShowPostingModal(false)}
          onSuccess={() => setShowPostingModal(false)}
        />
      )}
    </>
  );
}
